<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="storeProductSKU">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>New Product SKU</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length"
                             class="mb-3 bg-error"
                             closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['product_name']" autofocus
                                          :rules="[vRules.required, vRules.maxLength(64)]"
                                          density="compact" label="Product Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['sku']" :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="SKU"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['portals']" :items="portals"
                                      :rules="[vRules.required]" chips density="compact" item-title="name"
                                      item-value="id" label="Portals" multiple/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['master_configs']" :items="masterConfigs"
                                      :rules="[vRules.required]" chips density="compact" item-title="name"
                                      item-value="id" label="Master Configs" multiple/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['diagnostic_profile_id']" :items="profiles"
                                      density="compact" item-title="name" item-value="id"
                                      label="Select Diagnostic Profile"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['subscription_plan_id']" :items="plans"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="id"
                                      label="Default Subscription Plan"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['product_type']" :items="productTypes"
                                      :rules="[vRules.required]"
                                      density="compact" item-title="name" item-value="value" label="Product Type"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['primary_battery_type']" :items="batteryTypes"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="value"
                                      label="Primary Battery Type"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-file-input v-model="formData['picture']" density="compact"
                                          label="Device Picture"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['hw_revision']"
                                          :rules="[vRules.required, vRules.maxLength(32)]" density="compact"
                                          label="HW Revision"/>
                        </v-col>

                        <v-col cols="12" sm="12">
                            <v-text-field v-model="formData['manufacture_name']"
                                          :rules="[vRules.required, vRules.maxLength(64)]"
                                          density="compact" label="Manufacture Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['manufacture_code']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Manufacture Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['fcc_code']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="FCC Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['latest_firmware_1']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Latest Firmware 1"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['latest_firmware_2']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Latest Firmware 2"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['country_of_origin']"
                                          :rules="[vRules.required, vRules.maxLength(32)]"
                                          density="compact" label="Country of Origin"/>
                        </v-col>

                        <v-col cols="12">
                            <h4>Battery Channels</h4>
                            <div class="text-caption text-medium-emphasis">
                                The tracker always reports two voltage channels, voltage1 and voltage2. What
                                you set here is how the backend reads them: what each channel is, and which
                                one gets shown to users and used for battery alerts.
                            </div>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_fully_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 1 Fully Charge Threshold" type="number"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_low_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 1 Low Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_critical_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 1 Critical Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_fully_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 2 Fully Charge Threshold" type="number"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_low_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 2 Low Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_critical_charge_threshold']"
                                          :rules="[vRules.required]" density="compact" suffix="V"
                                          label="Battery 2 Critical Charge Threshold" type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery1_label']" density="compact"
                                          label="Battery 1 Label" placeholder="e.g. Super Cap"
                                          :hint="battery1Hint" maxlength="64"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['battery2_label']" density="compact"
                                          label="Battery 2 Label" placeholder="e.g. External Power"
                                          :hint="battery2Hint" maxlength="64"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['battery1_type']" :items="powerSourceTypes"
                                      clearable density="compact" item-title="name" item-value="value"
                                      label="Battery 1 Power Source Type"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['battery2_type']" :items="powerSourceTypes"
                                      clearable density="compact" item-title="name" item-value="value"
                                      label="Battery 2 Power Source Type"/>
                        </v-col>
                        <v-col cols="12">
                            <v-radio-group v-model="batteryDisplayChannel" hide-details inline
                                           label="Which battery to display">
                                <v-radio :label="channel1Label" :value="1"/>
                                <v-radio :label="channel2Label" :value="2"/>
                            </v-radio-group>
                            <div class="text-caption text-medium-emphasis">
                                Selects the voltage channel shown in the live view, the device list and the
                                low battery alert. Exactly one channel applies; the other is still recorded
                                but never surfaced.
                            </div>
                            <v-alert v-if="batteryChannelWarning" class="mt-3" density="compact"
                                     type="warning" variant="tonal">
                                {{ batteryChannelWarning }}
                            </v-alert>
                            <v-alert v-if="thresholdWarnings.length" class="mt-3" density="compact"
                                     type="warning" variant="tonal">
                                <p v-for="warning in thresholdWarnings" :key="warning">{{ warning }}</p>
                            </v-alert>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <h4>Features Supported</h4>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['wifi']" color="primary" label="Wifi"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['cellular']" color="primary" label="Cellular"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['ble']" color="primary" label="BLE"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['lora']" color="primary" label="LORA"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['ethernet']" color="primary" label="Ethernet"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['gateway']" color="primary" label="Gateway"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['gps']" color="primary" label="GPS"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['beacon']" color="primary" label="Beacon"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['external_sim1']" color="primary" label="External SIM1"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['external_sim2']" color="primary" label="External SIM2"/>
                        </v-col>

                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['internal_sim2']" color="primary" label="Internal SIM2"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['rechargeable']" color="primary" label="Rechargeable"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['super_cap']" color="primary" label="Super Cap"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['external_power']" color="primary" label="External Power"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['temp_sensor']" color="primary" label="Temp Sensor"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['humidity_sensor']" color="primary" label="Humidity Sensor"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['light_sensor']" color="primary" label="Light Sensor"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['solar']" color="primary" label="Solar"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['tamper_detection']" color="primary" label="Tamper Detection"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['nfc']" color="primary" label="NFC"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['magnet_switch']" color="primary" label="Magnet Switch"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['push_button']" color="primary" label="Push Button"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['cpuid_1']" color="primary" label="CPU ID 1"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['cpuid_2']" color="primary" label="CPU ID 2"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['obd_present']" color="primary" label="OBD Present"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch v-model="formData['carb_capable']" color="primary" label="CARB Capable"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSkuBatteryChannel} from "@/composables/useSkuBatteryChannel";
import {useSnackbarStore} from "@/stores/snackBarStore";

const formReady = ref(false);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    duplicateFrom: {type: Object, default: null},
});

const productTypes = [
    {
        value: 0,
        name: 'Tracker'
    },
    {
        value: 1,
        name: 'Beacon'
    },
    {
        value: 2,
        name: 'Gateway'
    },
    {
        value: 3,
        name: 'Location Assistant'
    },
    {
        value: 4,
        name: 'Driver Tag'
    },
    {
        value: 5,
        name: 'Solar MPPT Controller'
    }
];

const batteryTypes = [
    {value: 1, name: 'Alkaline'},
    {value: 2, name: 'Lithium Ion'},
    {value: 3, name: 'Lithium Polymer'},
    {value: 4, name: 'Nickel Metal Hydride'},
    {value: 5, name: 'Nickel Cadmium'},
    {value: 6, name: 'Lead Acid'},
    {value: 7, name: 'Zinc Carbon'},
    {value: 8, name: 'Silver Oxide'},
    {value: 9, name: 'Zinc Air'},
    {value: 10, name: 'Rechargeable Alkaline'},
    {value: 11, name: 'Primary Lithium'},
    {value: 12, name: 'Rechargeable Lithium'},
    {value: 13, name: 'Lithium Iron Phosphate'},
    {value: 14, name: 'Lithium Manganese Dioxide'},
    {value: 15, name: 'LNCAO (Lithium Nickel Cobalt)'},
    {value: 16, name: 'LNMCO (Lithium Nickel Manganese)'},
    {value: 17, name: 'Lithium Cobalt Oxide'},
    {value: 18, name: 'Lithium Titanate'},
    {value: 19, name: 'Sodium Ion'},
    {value: 20, name: 'Solid State'},
    {value: 21, name: 'Flow'},
    {value: 22, name: 'Thin Film'},
    {value: 23, name: 'Molten Salt'},
    {value: 24, name: 'Magnesium Ion'},
    {value: 25, name: 'Graphene'},
    {value: 26, name: 'Organic'},
    {value: 27, name: 'Zinc Manganese Dioxide'},
    {value: 28, name: 'Nickel Zinc'},
    {value: 29, name: 'Silver Zinc'},
    {value: 30, name: 'Dual Carbon'},
];

const powerSourceTypes = [
    {value: 0, name: 'Replaceable Non-Rechargeable Battery'},
    {value: 1, name: 'Non-Replaceable Non-Rechargeable Battery'},
    {value: 2, name: 'Non-Replaceable Rechargeable Battery'},
    {value: 3, name: 'External Power'},
    {value: 4, name: 'Super Capacitor'},
];

const formData = reactive({
    valid: false,
    portals: null,
    product_name: null,
    master_configs: null,
    subscription_plan_id: null,
    diagnostic_profile_id: null,
    sku: null,
    picture: null,
    hw_revision: null,
    manufacture_code: null,
    manufacture_name: null,
    product_type: null,
    primary_battery_type: null,
    fcc_code: null,
    latest_firmware_1: null,
    latest_firmware_2: null,
    country_of_origin: null,
    external_battery_low: null,
    wifi: null,
    cellular: null,
    ble: null,
    lora: null,
    ethernet: null,
    gateway: null,
    gps: null,
    beacon: null,
    external_sim1: null,
    external_sim2: null,
    internal_sim2: null,
    rechargeable: null,
    super_cap: null,
    external_power: null,
    temp_sensor: null,
    humidity_sensor: null,
    solar: null,
    tamper_detection: null,
    nfc: null,
    magnet_switch: null,
    light_sensor: null,
    push_button: null,
    cpuid_1: null,
    cpuid_2: null,
    obd_present: null,
    carb_capable: null,
    battery1_status: null,
    battery2_status: null,
    battery1_fully_charge_threshold: null,
    battery1_low_charge_threshold: null,
    battery1_critical_charge_threshold: null,
    battery2_fully_charge_threshold: null,
    battery2_low_charge_threshold: null,
    battery2_critical_charge_threshold: null,
    battery1_label: null,
    battery2_label: null,
    battery1_type: null,
    battery2_type: null,
});

const {
    channel1Label,
    channel2Label,
    batteryDisplayChannel,
    batteryChannelWarning,
    thresholdWarnings,
    battery1Hint,
    battery2Hint,
} = useSkuBatteryChannel(formData);

const emits = defineEmits(['update:modelValue', 'addSku']);
const dialogSwitch = ref(props.modelValue);
const masterConfigs = ref([]);
const profiles = ref([]);
const portals = ref([]);
const errors = ref([]);
const plans = ref([]);

onMounted(() => {
    Promise.all([fetchPlans(), fetchPortals(), fetchProfiles(), fetchMasterConfigs()])
        .finally(() => formReady.value = true);

    if (props.duplicateFrom) {
        const s = props.duplicateFrom;
        const fields = [
            'product_name', 'hw_revision', 'manufacture_code', 'manufacture_name',
            'product_type', 'primary_battery_type', 'fcc_code', 'latest_firmware_1',
            'latest_firmware_2', 'country_of_origin', 'subscription_plan_id', 'diagnostic_profile_id',
            'battery1_fully_charge_threshold', 'battery1_low_charge_threshold', 'battery1_critical_charge_threshold',
            'battery2_fully_charge_threshold', 'battery2_low_charge_threshold', 'battery2_critical_charge_threshold',
            'battery1_label', 'battery2_label',
            'battery1_type', 'battery2_type',
        ];
        fields.forEach(f => { if (s[f] != null) formData[f] = s[f]; });
        const boolFields = [
            'wifi', 'cellular', 'ble', 'lora', 'ethernet', 'gateway', 'gps', 'beacon',
            'external_sim1', 'external_sim2', 'internal_sim2', 'rechargeable', 'super_cap',
            'external_power', 'temp_sensor', 'humidity_sensor', 'solar', 'tamper_detection',
            'nfc', 'magnet_switch', 'light_sensor', 'push_button', 'cpuid_1', 'cpuid_2', 'obd_present',
            'carb_capable', 'battery1_status', 'battery2_status',
        ];
        boolFields.forEach(f => formData[f] = !!s[f]);
        if (s.portals) formData.portals = s.portals.map(p => p.id);
        if (s.master_configs) formData.master_configs = s.master_configs.map(c => c.id);
        // SKU code left empty - user must provide a unique one
    }
})
const fetchProfiles = () => {
    return send('get', `admin/diagnostics/profiles?list=true`)
        .then((response) => {
            profiles.value = response.data.data;
        })
        .catch((error) => {
            showSnackbar('Diagnostic Profiles List could not be loaded', 'error')
        })
}
const fetchPlans = () => {
    return send('get', `subscription-plans/list`)
        .then((response) => {
            plans.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Subscription plans List could not be loaded', 'error')
    })
}
const fetchPortals = () => {
    return send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Portals List could not be loaded', 'error')
    })
}
const fetchMasterConfigs = () => {
    return send('post', `admin/device-configs-list?master=1`)
        .then((response) => {
            masterConfigs.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Master config files List could not be loaded', 'error')
    })
}
const storeProductSKU = () => {
    errors.value = [];
    if (formData['picture']) {
        formData['picture'] = formData['picture'];
    }
    send('post', `product-skus`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addSku', response.data.data.product_sku);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
